import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Paper, CircularProgress, Alert, Snackbar, Table, TableHead, TableBody, TableRow, TableCell, Button, FormControlLabel, Checkbox, Link, TextField } from "@mui/material";
import { useParams } from "react-router-dom";
import RegexPatternForm from "../Shared/RegexPatternForm";
import { useVendor, useGetMatchedMemos, useUpdateVendorEnhancements, useVendorMutation, useStartVendorMemoTester } from "../../hooks/useVendors";
import { useGetMatchedVendors } from "../Brands/hooks";
import BackButton from "../Shared/BackButton";

export default function MemoTester() {
  const { id } = useParams();
  const { data, isLoading, error } = useVendor(id);

  // Use useMemo to prevent recreating these objects on every render
  const vendor = useMemo(() => data?.vendor ?? {}, [data]);
  const correctedVendors = useMemo(() => data?.correctedVendors ?? [], [data]);
  const correctedVendorIds = useMemo(() => correctedVendors.map(v => v.id), [correctedVendors]);

  const startVendorMemoTester = useStartVendorMemoTester();
  const updateVendorEnhancements = useUpdateVendorEnhancements(id);
  const updateVendor = useVendorMutation();

  const [formData, setFormData] = useState({
    regexStart: "",
    regexMatch: "",
    regexEnd: "",
    negativeTerms: ""
  });
  const [regexPattern, setRegexPattern] = useState("");
  const [primaryVendorData, setPrimaryVendorData] = useState({
    matchRegex: "",
    correctedVendorIds: []
  });

  // Fix the useEffect to only run once when vendor is loaded
  useEffect(() => {
    // Only set the initial data once when vendor data is first loaded
    if (vendor && vendor.matchRegex && !primaryVendorData.matchRegex) {
      setPrimaryVendorData({
        matchRegex: vendor.matchRegex || "",
        correctedVendorIds
      });
    }
  }, [vendor, correctedVendorIds, primaryVendorData.matchRegex]);

  const [isTestingRegex, setIsTestingRegex] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [matchedVendors, setMatchedVendors] = useState([]);
  const { data: matchedVendorsData, isLoading: isLoadingMatchedVendors } = useGetMatchedVendors(id, false);
  const { data: matchedMemosData, isLoading: isLoadingMatchedMemos } = useGetMatchedMemos(id);

  useEffect(() => {
    if (matchedVendorsData?.error === "No matching vendors found") {
      setMatchedVendors([]);
    } else if (matchedVendorsData?.matchedVendors) {
      setMatchedVendors(matchedVendorsData.matchedVendors.map(vendor => ({
        id: vendor.vendorId,
        vendorName: vendor.vendorName,
        joroSubcategory: vendor.joroSubcategory,
        joroSubcategoryId: vendor.joroSubcategoryId,
        transactionCount: vendor.transactionCount,
        isCleanVendorName: vendor.isCleanVendorName,
        correctedVendorId: vendor.correctedVendorId
      })));
    }
  }, [matchedVendorsData]);

  const handleTestRegex = async (pattern) => {
    setIsTestingRegex(true);
    setRegexPattern(pattern);
    try {
      const response = await startVendorMemoTester.mutateAsync({ vendorId: id, pattern });
      if (response.error) {
        setSnackbar({
          open: true,
          message: response.error,
          severity: "error"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Memo tester job started successfully. Checking for matches...",
          severity: "success"
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to start memo tester",
        severity: "error"
      });
    } finally {
      setIsTestingRegex(false);
    }
  };

  const handleUpdateVendor = async () => {
    const trimmedRegex = primaryVendorData.matchRegex.trim();

    if (!trimmedRegex) {
      setSnackbar({
        open: true,
        message: "Regex pattern cannot be blank. Please enter a valid regex.",
        severity: "error"
      });
      return;
    }
    try {
      const updateData = {
        cleanVendorDetails: { matchRegex: primaryVendorData.matchRegex },
        correctedVendorIds: primaryVendorData.correctedVendorIds
      };
      const response = await updateVendorEnhancements.mutateAsync(updateData);
      if (response.error) {
        setSnackbar({
          open: true,
          message: response.error,
          severity: "error"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Vendor regex pattern updated successfully",
          severity: "success"
        });
        setPrimaryVendorData({
          matchRegex: response.cleanedVendor.matchRegex,
          correctedVendorIds: response.correctedVendors.map(v => v.id)
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to update vendor regex pattern",
        severity: "error"
      });
    }
  };

  const handleChooseCleanVendor = async (cleanVendorId) => {
    try {
      const response = await updateVendor.mutateAsync({
        vendorId: id,
        update: {
          correctedVendorId: cleanVendorId
        }
      });

      if (response.error) {
        setSnackbar({
          open: true,
          message: response.error,
          severity: "error"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Vendor updated successfully",
          severity: "success"
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to update vendor",
        severity: "error"
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={4}>
        <Alert severity="error">
          Error loading vendor: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box m={4}>
      <Typography variant="h1" gutterBottom sx={{ pt: 2 }}>
        Memo Tester - {vendor.vendorName}
      </Typography>
      <BackButton />
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box mb={3}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" color="textSecondary">
              Vendor Match Regex:
            </Typography>
            <TextField
              size="small"
              value={primaryVendorData.matchRegex}
              placeholder="None"
              sx={{ minWidth: 300 }}
              onChange={(e) => {
                setPrimaryVendorData(prev => ({
                  ...prev,
                  matchRegex: e.target.value
                }));
              }}
            />
          </Box>
          <Box>
            <Typography variant="body1" color="textSecondary">
              Update the vendor regex and set this vendor as the corrected vendor ID for the other vendors selected below:
            </Typography>
            <br />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleUpdateVendor(primaryVendorData.matchRegex)}
                data-testid="update-vendor-regex-button"
                disabled={!primaryVendorData.matchRegex.trim()}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
      <Paper elevation={3} sx={{ p: 3 }}>
        <RegexPatternForm
          formData={formData}
          onFormChange={setFormData}
          onTestRegex={handleTestRegex}
          isTestingRegex={isTestingRegex}
          regexPattern={regexPattern}
        />
      </Paper>
      {
        isLoadingMatchedVendors
          ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )
          : matchedVendors.length > 0
            ? (
              <Box mb={4}>
                <Typography variant="h2" gutterBottom sx={{ pt: 2 }}>
              Vendors matched to the regex pattern
                </Typography>

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor ID</TableCell>
                      <TableCell>Vendor Name</TableCell>
                      <TableCell>Subcategory</TableCell>
                      <TableCell>Txn count</TableCell>
                      <TableCell>Clean vendor</TableCell>
                      {!vendor.isCleanVendorName && (
                        <TableCell sx={{ width: "200px" }}>Choose this vendor as the corrected vendor for {vendor.vendorName}</TableCell>
                      )}
                      <TableCell>Corrected vendor ID</TableCell>
                      <TableCell sx={{ width: "200px" }}>Set {vendor.vendorName} as the corrected vendor for this vendor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matchedVendors.map((matchedVendor) => (
                      <TableRow key={`${matchedVendor.id}-${matchedVendor.vendorName}`}>
                        <TableCell>
                          <Link href={`/vendors/${matchedVendor.id}`}>
                            {matchedVendor.id}
                          </Link>
                        </TableCell>
                        <TableCell>{matchedVendor.vendorName}</TableCell>
                        <TableCell>{matchedVendor.joroSubcategory}</TableCell>
                        <TableCell>
                          <Link href={`/vendors/${matchedVendor.id}/transactions`}>
                            {matchedVendor.transactionCount}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {matchedVendor.isCleanVendorName ? "yes" : "no"}
                        </TableCell>
                        {!vendor.isCleanVendorName && (
                          <TableCell>
                            {matchedVendor.isCleanVendorName && (
                              <Button
                                variant="contained"
                                size="small"
                                color="secondary"
                                onClick={() => handleChooseCleanVendor(matchedVendor.id)}
                              >
                            Choose
                              </Button>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {primaryVendorData.correctedVendorIds?.includes(matchedVendor.id)
                            ? "this vendor"
                            : (
                              <Link href={`/vendors/${matchedVendor.correctedVendorId}`}>
                                {matchedVendor.correctedVendorId}
                              </Link>
                            )}
                        </TableCell>
                        <TableCell>
                          {(!matchedVendor.isCleanVendorName && (matchedVendor.correctedVendorId === vendor.id || matchedVendor.correctedVendorId === null)) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={primaryVendorData.correctedVendorIds?.includes(matchedVendor.id)}
                                  onChange={() => {
                                    setPrimaryVendorData(prev => ({
                                      ...prev,
                                      correctedVendorIds: prev.correctedVendorIds?.includes(matchedVendor.id)
                                        ? prev.correctedVendorIds.filter(id => id !== matchedVendor.id)
                                        : [...(prev.correctedVendorIds || []), matchedVendor.id]
                                    }));
                                  }}
                                />
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )
            : (
              <Box mb={4}>
                <Typography variant="h3" gutterBottom>
              No vendors matched
                </Typography>
              </Box>
            )
      }

      {
        isLoadingMatchedMemos
          ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )
          : matchedMemosData?.matchedMemos?.length > 0
            ? (
              <Box mb={4}>
                <Typography variant="h2" gutterBottom sx={{ pt: 2 }}>
              Summary of matched memos
                </Typography>
                <Typography variant="h4" gutterBottom sx={{ pt: 2, fontStyle: "italic" }}>
              All memos have been cleaned up and de-duplicated
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Memo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matchedMemosData.matchedMemos.map((memo, index) => (
                      <TableRow key={`${memo.memo}-${index}`}>
                        <TableCell>{memo.memo}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )
            : (
              <Box mb={4}>
                <Typography variant="h4" gutterBottom>
              No memos matched
                </Typography>
              </Box>
            )
      }

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
